import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup } from '@fortawesome/sharp-solid-svg-icons';
import { Map } from '@maptiler/sdk';
import maplibregl from 'maplibre-gl';
import { usePathname } from 'next/navigation';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonVariant } from '@/components/button';
import { InsightsIcon } from '@/components/icons/InsightsIcon';
import OutsideClickWrapper from '@/components/OutsideClickWrapper';
import { AcresFeatureFlags } from '@/constants/constants';
import { CUSTOMIZE_LAND_PLAT_MAP_URL } from '@/constants/urls';
import { useThreeDController } from '@/features/3d/3DControllerProvider';
import { useInsightsCatalogContext } from '@/features/insightsCatalog/provider';
import { useLayerLibraryContext } from '@/features/layerLibrary/provider';
import { InsightsOverlayPopover } from '@/features/navigation/bottom-navigation/toolkit/insightsOverlayPopover';
import { LayerLibraryOverlay } from '@/features/navigation/bottom-navigation/toolkit/layerLibraryOverlay';
import { useGetFeatureFlag } from '@/hooks/useGetFeatureFlag';
import { useAuth } from '@/providers/auth-provider';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';

import { LayersOverlay } from './layersOverlay';
import MapCompass from './mapCompass';
import ZoomToggle from './mapZoomToggle';
import { ParcelFsaSelector } from './parcelFsaSelector';
import { QuickMeasureTool } from './quickMeasure';
import TerrainExaggerationSlider from './terrainExaggerationSlider';
import ThreeDimensionToggle from './threeDimensionToggle';

type Props = {
  map: maplibregl.Map | undefined;
};

const routesToHideParcelFSASelector = [CUSTOMIZE_LAND_PLAT_MAP_URL];

const ToolKit: FunctionComponent<Props> = ({ map }) => {
  const pathname = usePathname();
  const { mapInstance } = useMapboxContext();
  const { isAnonymous } = useAuth();
  const { userCanAccessElevation, userCanAccessSoils, userCanAccessFlood } =
    useAuth();
  const { legendLayer, setLegendLayerId } = useInsightsCatalogContext();
  const { legendLayer: llLegendLayer, setLegendLayerId: llSetLegendLayerId } =
    useLayerLibraryContext();
  const { toggleTerrain, terrainEnabled } = useThreeDController();
  const [overlayOpen, setOverlayOpen] = useState<boolean>(false);
  const [layersOpen, setLayersOpen] = useState<boolean>(false);
  const isToolbarActive = useGetFeatureFlag(
    AcresFeatureFlags.TEMP_TOOL_TOP_BAR
  );
  const isNewLayersActive = useGetFeatureFlag(
    AcresFeatureFlags.TEMP_NEW_LAYERS
  );

  const [quickMeasureOverlayOpen, setQuickMeasureOverlayOpen] =
    useState<boolean>(false);

  const hideParcelFSASelector = useMemo(() => {
    return routesToHideParcelFSASelector.some((val) => pathname?.includes(val));
  }, [pathname]);

  useEffect(() => {
    if (legendLayer || llLegendLayer) {
      setOverlayOpen(false);
    }
  }, [legendLayer, llLegendLayer]);

  const handleQuickMeasureClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      if (overlayOpen) setOverlayOpen(false);
      if (layersOpen) setLayersOpen(false);
      setLegendLayerId(null);
      llSetLegendLayerId(null);
      setQuickMeasureOverlayOpen((prev) => !prev);
    },
    [overlayOpen, layersOpen, setLegendLayerId, llSetLegendLayerId]
  );

  const showMapSettingsCog = useMemo(
    () => userCanAccessElevation || userCanAccessFlood || userCanAccessSoils,
    [userCanAccessElevation, userCanAccessFlood, userCanAccessSoils]
  );

  return (
    <OutsideClickWrapper
      className="z-20 flex items-end gap-3 transition-all"
      onOutsideClick={() => {
        setOverlayOpen(false);
      }}
    >
      <span className="flex w-[32px] shrink flex-col space-y-3 antialiased">
        <MapCompass />
        {terrainEnabled && (
          <TerrainExaggerationSlider map={mapInstance as Map} />
        )}
        <ThreeDimensionToggle
          is3DEnabled={terrainEnabled}
          toggle3DMode={toggleTerrain}
        />
        {!isToolbarActive && (
          <QuickMeasureTool
            map={map}
            open={quickMeasureOverlayOpen}
            onClick={handleQuickMeasureClick}
          />
        )}

        {!isAnonymous && (
          <Button
            className={twMerge(
              'flex h-8 w-8 items-center justify-center rounded-sm bg-white p-[6px] text-gray-70',
              'hover:bg-green-50 hover:text-white active:text-white',
              layersOpen && 'bg-green-50 text-white'
            )}
            data-tip="Layers"
            variant={ButtonVariant.PRIMARY_LIGHT}
            onClick={(e) => {
              e.stopPropagation();
              if (overlayOpen) setOverlayOpen(false);
              if (quickMeasureOverlayOpen) setQuickMeasureOverlayOpen(false);

              setLegendLayerId(null);
              setLegendLayerId(null);
              setLayersOpen((prev) => !prev);
            }}
          >
            <FontAwesomeIcon icon={faLayerGroup} />
          </Button>
        )}

        {showMapSettingsCog && (
          <Button
            className={twMerge(
              'flex h-8 w-8 items-center justify-center rounded-sm bg-white p-[6px] text-gray-70',
              'hover:bg-green-50 hover:text-white active:text-white',
              overlayOpen && 'bg-green-50 text-white'
            )}
            data-tip="Insight Overlays"
            variant={ButtonVariant.PRIMARY_LIGHT}
            onClick={(e) => {
              e.stopPropagation();
              if (quickMeasureOverlayOpen) setQuickMeasureOverlayOpen(false);
              if (layersOpen) setLayersOpen(false);
              setLegendLayerId(null);
              llSetLegendLayerId(null);
              setOverlayOpen((prev) => !prev);
            }}
          >
            <InsightsIcon color="currentColor" />
          </Button>
        )}
        {/* TODO: remove later, if we don't want this back, keep it commented for now because it might come back */}
        {/* <CurrentLocation map={map} /> */}
        <ZoomToggle map={map} />
        {!hideParcelFSASelector && <ParcelFsaSelector />}
      </span>

      <InsightsOverlayPopover open={overlayOpen} />
      {!isAnonymous && !isNewLayersActive && (
        <LayersOverlay
          // This is an OR so that the layers overlay stays open ONLY when it is set to stay opn
          //  other pages can set and remove legendLayer and we expect this to close fully
          open={layersOpen || !!legendLayer}
          setOpen={setLayersOpen}
        />
      )}

      {!isAnonymous && isNewLayersActive && (
        <LayerLibraryOverlay
          // This is an OR so that the layers overlay stays open ONLY when it is set to stay opn
          //  other pages can set and remove legendLayer and we expect this to close fully
          open={layersOpen || !!llLegendLayer}
          setOpen={setLayersOpen}
        />
      )}
    </OutsideClickWrapper>
  );
};
export default ToolKit;
